<!-- eslint-disable vuejs-accessibility/label-has-for -->
<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<script setup lang="ts">
import type { CSSProperties } from 'vue';

import type { Field, Theme } from './report-typing';

import { useUpdatedReportStore } from '@src/store/report-store';
import {
  computed,

  nextTick,
  onMounted,
  ref,
  watch,
} from 'vue';

import DateSelect from './DateSelect.vue';
import DropdownSelect from './DropdownSelect.vue';

const props = defineProps<{
  field: Field;
}>();

const reportStore = useUpdatedReportStore();
const theme = computed<Theme | null>(() => {
  const loadedTemplate = reportStore.getTemplate();
  return loadedTemplate?.theme ?? null;
});

// ----- Computed state from store -----
const currentValue = computed(() => reportStore.getValue(props.field.key));

const textInputValue = computed(() => {
  return typeof currentValue.value === 'string' ? currentValue.value : '';
});

const numberInputValue = computed<string | number>(() => {
  const val = currentValue.value;
  if (typeof val === 'number' || typeof val === 'string') return val;
  return '';
});

// Textarea auto-resize
const textareaRef = ref<HTMLTextAreaElement | null>(null);

function autoResize() {
  const el = textareaRef.value;
  if (el) {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }
}

watch(currentValue, () => {
  nextTick(autoResize);
});

onMounted(() => {
  nextTick(autoResize);
});

const textareaStyle = computed<CSSProperties>(() => {
  return {
    lineHeight: '1.25',
    paddingTop: '2px',
    paddingBottom: '2px',
  };
});

const displayValue = computed(() => {
  const val = currentValue.value;
  if (props.field.type === 'dropdown') {
    const opts = props.field.options || [];
    const match = opts.find(o => o.id === val);
    return match ? match.label : val || '—';
  }
  if (props.field.type === 'date') {
    if (!val) return '—';
    const [year, month, day] = String(val).split('-');
    const months = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];
    const mIdx = Number(month) - 1;
    return `${day}-${months[mIdx] || 'Mon'}-${year}`;
  }
  let disp = '—';
  if (val !== null && val !== undefined && val !== '') {
    disp = String(val);
  }
  else if (props.field.defaultValue === '') {
    disp = '';
  }
  if (props.field.unit && val !== null && val !== undefined && val !== '') {
    disp = `${disp} ${props.field.unit}`;
  }
  return disp;
});

const isDisabled = computed(() => {
  // Check store FieldState first, fall back to field definition
  const storeState = reportStore.values?.[props.field.key];
  if (storeState?.isDisabled !== undefined) return storeState.isDisabled;
  return props.field.isDisabled ?? false;
});

const isEditable = computed(() => {
  const storeState = reportStore.values?.[props.field.key];
  if (storeState?.isEditable !== undefined) return storeState.isEditable;
  return props.field.isEditable ?? true;
});

// ----- Theme-driven inline styles -----
const labelStyle = computed<CSSProperties>(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.label.family,
    fontSize: `${theme.value.fonts.label.size}px`,
    fontWeight: theme.value.fonts.label.weight,
    color: theme.value.colors.text,
    lineHeight: '1',
  };
});

const valueStyle = computed<CSSProperties>(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.value.family,
    fontSize: `${theme.value.fonts.value.size}px`,
    fontWeight: theme.value.fonts.value.weight,
    color: theme.value.colors.text,
    lineHeight: '1.25',
    marginTop: '3px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  };
});

const inputStyle = computed<CSSProperties>(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.value.family,
    fontSize: `${theme.value.fonts.value.size}px`,
    fontWeight: theme.value.fonts.value.weight,
    color: theme.value.colors.text,
    borderColor: theme.value.colors.border,
    borderRadius: `${theme.value.borderRadius}px`,
  };
});

const bodyStyle = computed<CSSProperties>(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.body.family,
    fontSize: `${theme.value.fonts.body.size}px`,
    fontWeight: theme.value.fonts.body.weight,
    color: theme.value.colors.text,
  };
});

const mutedStyle = computed<CSSProperties>(() => {
  if (!theme.value) return {};
  return {
    fontFamily: theme.value.fonts.label.family,
    fontSize: `${theme.value.fonts.label.size}px`,
    color: theme.value.colors.muted,
  };
});

const imagePlaceholderStyle = computed<CSSProperties>(() => {
  if (!theme.value) return {};
  return {
    borderStyle: theme.value.imagePlaceholder.borderStyle,
    borderColor: theme.value.imagePlaceholder.borderColor,
    background: theme.value.imagePlaceholder.background,
    borderRadius: `${theme.value.borderRadius}px`,
  };
});

// ----- Event handlers -----
function onTextInput(e: Event) {
  const target = e.target as HTMLInputElement;
  reportStore.setValue(props.field.key, target.value);
}

function onNumberInput(e: Event) {
  const target = e.target as HTMLInputElement;
  const num = target.value === '' ? null : Number(target.value);
  reportStore.setValue(props.field.key, num);
}

function onBooleanChange(e: Event) {
  const target = e.target as HTMLInputElement;
  reportStore.setValue(props.field.key, target.checked);
}
</script>

<template>
  <div class="field-renderer" :class="{ 'field-disabled': isDisabled }">
    <!-- Label -->
    <label v-if="field.label" class="field-label" :style="labelStyle">
      {{ field.label }}
      <span v-if="field.isRequired" class="field-required">*</span>
    </label>

    <!-- VIEW MODE (read-only/disabled) -->
    <div
      v-if="
        isDisabled
          && ['text', 'number', 'date', 'dropdown'].includes(field.type)
      "
      class="field-readonly-value"
      :style="valueStyle"
    >
      {{ displayValue }}
    </div>

    <!-- EDIT MODE -->
    <template v-else>
      <!-- TEXT -->
      <textarea
        v-if="field.type === 'text'"
        ref="textareaRef"
        class="field-input field-textarea"
        :style="[inputStyle, textareaStyle]"
        :value="textInputValue"
        :placeholder="field.placeholder || ''"
        :readonly="!isEditable"
        rows="1"
        @input="onTextInput"
      />

      <!-- NUMBER -->
      <div v-else-if="field.type === 'number'" class="field-number-wrap">
        <input
          type="number"
          class="field-input"
          :style="inputStyle"
          :value="numberInputValue"
          :placeholder="field.placeholder || ''"
          :readonly="!isEditable"
          @input="onNumberInput"
        >
        <span v-if="field.unit" class="field-unit" :style="mutedStyle">
          {{ field.unit }}
        </span>
      </div>

      <!-- DATE -->
      <DateSelect
        v-else-if="field.type === 'date'"
        :field-key="field.key"
        :editable="isEditable"
      />

      <!-- DROPDOWN -->
      <DropdownSelect
        v-else-if="field.type === 'dropdown'"
        :field-key="field.key"
        :editable="isEditable"
      />

      <!-- BOOLEAN -->
      <label v-else-if="field.type === 'boolean'" class="field-checkbox-label">
        <input
          type="checkbox"
          :checked="!!currentValue"
          :disabled="isDisabled"
          @change="onBooleanChange"
        >
        <span :style="bodyStyle">{{ field.placeholder || '' }}</span>
      </label>

      <!-- IMAGE (placeholder for now) -->
      <div
        v-else-if="field.type === 'image'"
        class="field-image-placeholder"
        :style="imagePlaceholderStyle"
      >
        <span :style="mutedStyle">{{ field.placeholder || 'Image' }}</span>
      </div>

      <!-- FALLBACK READONLY -->
      <div v-else class="field-readonly" :style="bodyStyle">
        {{ currentValue ?? '—' }}
      </div>
    </template>
  </div>
</template>

<style scoped>
.field-renderer {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.field-disabled {
  pointer-events: none;
}

.field-label {
  font-size: 13px;
  color: #2d2d2d;
  font-weight: 500;
}

.field-required {
  color: #e55;
  margin-left: 2px;
}

.field-input {
  width: 100%;
  border: 1px solid #ddd;
  background: #fff;
  font-weight: 700;
  padding: 0px 4px;
  font-size: 13px;
  color: black;
  box-sizing: border-box;
  outline: none;
}

.field-input:focus {
  border-color: #0284c7;
}

.field-input[readonly] {
  background: transparent;
  cursor: default;
}

.field-input:disabled {
  background-color: transparent;
  border: none;
  padding: 0;
  font-weight: 900;
}

.field-number-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-number-wrap .field-input {
  flex: 1;
}

.field-unit {
  font-size: 11px;
  color: #888;
  white-space: nowrap;
}

.field-checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.field-image-placeholder {
  width: 100%;
  min-height: 60px;
  border-width: 2px;
  border-style: dashed;
  border-color: #cbd5e1;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.field-readonly {
  font-size: 13px;
  color: #444;
  font-weight: 700;
}

textarea.field-input {
  resize: none;
  overflow: hidden;
  display: block;
  font-family: inherit;
}
</style>
